import { FLAGS } from 'constants/flags';
import { useFeature, useLocalStorage } from 'modules/utils';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isVisitingFromMobile } from 'utils/device';
import {
  generateNavigationURL,
  type NavigationApp,
  type NavigationDestination,
} from 'utils/map';
import { NavigationAppsBottomSheet } from './NavigationAppsBottomSheet';

const DEFAULT_NAVIGATION_APP: NavigationApp = 'googleMaps';
const NAVIGATION_APP_STORAGE_KEY = 'hiboo__navigationApp';

const openNavigationURL = (
  destination: NavigationDestination,
  app: NavigationApp,
) => {
  window.open(
    generateNavigationURL({ destination, app }),
    '_blank',
    'noopener,noreferrer',
  );
};

type NavigationLinkProps = {
  to: string;
  target: '_blank';
  rel: string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

type NavigationAppsContextType = {
  openNavigation: (destination: NavigationDestination) => void;
  getNavigationLinkProps: (
    destination: NavigationDestination,
    options?: { onNavigate?: () => void },
  ) => NavigationLinkProps;
};

const createNavigationLinkPropsGetter =
  (
    openNavigation: (destination: NavigationDestination) => void,
    app: NavigationApp = DEFAULT_NAVIGATION_APP,
  ): NavigationAppsContextType['getNavigationLinkProps'] =>
  (destination, { onNavigate } = {}) => ({
    to: generateNavigationURL({ destination, app }),
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: (event) => {
      event.preventDefault();
      onNavigate?.();
      openNavigation(destination);
    },
  });

// Without the provider (e.g. in tests), fall back to opening the default app directly
const fallbackOpenNavigation = (destination: NavigationDestination) =>
  openNavigationURL(destination, DEFAULT_NAVIGATION_APP);

const NavigationAppsContext = createContext<NavigationAppsContextType>({
  openNavigation: fallbackOpenNavigation,
  getNavigationLinkProps: createNavigationLinkPropsGetter(
    fallbackOpenNavigation,
  ),
});

/**
 * Renders the single navigation apps bottom sheet for the whole app. On a
 * mobile device with the `waze-navigation` flag enabled, `openNavigation`
 * opens the sheet to let the user pick their navigation app (Google Maps or
 * Waze). Otherwise, it opens Google Maps directly. The user can persist their
 * choice from the sheet, in which case it opens directly on later calls.
 */
export const NavigationAppsProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const [wazeNavigationFlag] = useFeature<boolean>(FLAGS.WAZE_NAVIGATION);
  const [pendingDestination, setPendingDestination] =
    useState<NavigationDestination | null>(null);
  const [persistedApp, setPersistedApp] = useLocalStorage<NavigationApp | null>(
    NAVIGATION_APP_STORAGE_KEY,
    null,
  );

  const openNavigation = useCallback(
    (target: NavigationDestination) => {
      const canChooseApp = wazeNavigationFlag && isVisitingFromMobile();

      if (!canChooseApp) {
        openNavigationURL(target, DEFAULT_NAVIGATION_APP);
        return;
      }

      if (persistedApp) {
        openNavigationURL(target, persistedApp);
        return;
      }

      setPendingDestination(target);
    },
    [wazeNavigationFlag, persistedApp],
  );

  const value = useMemo(
    () => ({
      openNavigation,
      getNavigationLinkProps: createNavigationLinkPropsGetter(
        openNavigation,
        persistedApp ?? DEFAULT_NAVIGATION_APP,
      ),
    }),
    [openNavigation, persistedApp],
  );

  return (
    <NavigationAppsContext.Provider value={value}>
      {children}
      {pendingDestination && (
        <NavigationAppsBottomSheet
          onSelect={(app, remember) => {
            if (remember) {
              setPersistedApp(app);
            }
            openNavigationURL(pendingDestination, app);
            setPendingDestination(null);
          }}
          onDismiss={() => setPendingDestination(null)}
        />
      )}
    </NavigationAppsContext.Provider>
  );
};

export const useNavigationApps = () => useContext(NavigationAppsContext);
